import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { createNews, getNewsByID, updateNews } from "./services/news.services";
import { clearNewsDetails } from "./newsSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateNews: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const NewsID = location.state?.NewsID;
  const NewsDetail = useAppSelector((state) => state.news.NewsDetail);
  const loading = useAppSelector((state) => state.news.loading);

  useEffect(() => {
    if (NewsID) {
      dispatch(getNewsByID(NewsID));
    }
    return () => {
      dispatch(clearNewsDetails());
    };
  }, [NewsID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      NewsTitle: values.NewsTitle ? values.NewsTitle : "",
      NewsDate: values.NewsDate
        ? moment(values.NewsDate).format("YYYY-MM-DD")
        : null,
      ShortDescription: values.ShortDescription ? values.ShortDescription : "",
      LongDescription: values.LongDescription ? values.LongDescription : "",
      NewsURL: values.NewsURL ? values.NewsURL : "",
      MetaTitle: values.MetaTitle ? values.MetaTitle : "",
      MetaDescription: values.MetaDescription ? values.MetaDescription : "",
      IsActive: values.IsActive === 1,
    };
    try {
      if (NewsID) {
        const editPayload = {
          ...formData,
          ID: NewsID,
        };

        const response = await dispatch(updateNews(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/news");
        }
      } else {
        const response = await dispatch(createNews(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/news");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={NewsDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 40 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {NewsID ? "Update News" : "Create News"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="NewsTitle"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="NewsURL"
                      label="News URL"
                      placeholder="News URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="NewsDate"
                      label="News Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={2}
                    rowSpan={3}
                    style={{ width: "100%" }}
                  >
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormTextArea}
                      astrike={true}
                      style={{ height: 130 }}
                      validator={requiredValidator}
                    />
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    rowSpan={3}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      component={FormRichTextField}
                      style={{ height: 310 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={NewsID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/news")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateNews;
